import React, { useEffect } from "react";
import classnames from "classnames";
import styles from "./Sheet.module.scss";

export interface SheetProps {
    open: boolean;
    /** Закрыть: касание затемнения, Esc */
    onClose: () => void;
    title?: string;
    children?: React.ReactNode;
    /** Без привязки к экрану — для витрины */
    inline?: boolean;
}

/**
 * Шторка снизу: затемнение, ручка, заголовок, содержимое. Нижний отступ — под полосу жестов.
 *
 * Сама шторка историю браузера не трогает. Системный жест «назад» должен закрывать её раньше
 * перехода между экранами (FR-016) — это делает хук `useSheetHistory` оболочки: он хранит
 * открытость шторки в записи истории и зовёт `onClose` по возврату.
 */
export const Sheet = ({ open, onClose, title, children, inline }: SheetProps) => {
    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        window.addEventListener("keydown", onKeyDown);

        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, onClose]);

    if (!open) {
        return null;
    }

    return (
        <div className={classnames(styles.overlay, { [styles.inline]: inline })}>
            <button type="button" className={styles.backdrop} aria-label="Закрыть" onClick={onClose} tabIndex={-1} />
            <div className={styles.sheet} role="dialog" aria-modal="true" aria-label={title}>
                <div className={styles.grip} aria-hidden="true" />
                {title && <h2 className={styles.title}>{title}</h2>}
                {children}
            </div>
        </div>
    );
};
